import React, { useMemo } from "react";
import {
  Button, Divider, Spin, Table, Tooltip,
} from "antd";
import { /* ReloadOutlined,  */DownloadOutlined } from "@ant-design/icons";
import { startCase } from "lodash";
import Plot from "react-plotly.js";
import Scrollbar from "react-scrollbars-custom";
import moment from "moment";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import { downloadFileFromS3 } from "../../../utils/helpers";
import { DEFAULT_DATE_FORMAT, PROMPT_STATUS_CONFIG } from "./utils";

import "./AIAnalyticsPromptResultPanel.css";

/** Returns true when a table cell value is a nested object. @param {*} value - cell value */
const isObjectValue = (value) => value !== null && typeof value === "object";
const MAX_TABLE_COLUMNS = 8;
const QUERY_TABLE_PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_QUERY_TABLE_PAGE_SIZE = 20;
const DEFAULT_PLOT_HEIGHT = 480;

/** Builds a Plotly layout that preserves API height and avoids clipped axes or legends. @param {Object} layout - raw layout from plotly_chart.json */
const buildPlotLayout = (layout = {}) => {
  const {
    width: _layoutWidth,
    margin: layoutMargin,
    legend,
    ...restLayout
  } = layout;
  const margin = {
    l: 80,
    r: 80,
    t: 80,
    b: 80,
    ...layoutMargin,
  };

  return {
    ...restLayout,
    autosize: true,
    automargin: true,
    height: layout.height || DEFAULT_PLOT_HEIGHT,
    margin: { ...margin },
  };
};

/** Formats a prompt timestamp for the result panel. @param {string|null} value - raw timestamp from API */
const formatTimestamp = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DEFAULT_DATE_FORMAT) : "";
};

/** Returns true when a column key represents a date field. @param {string} key - column field name */
const isDateColumnKey = (key) => String(key).toLowerCase().includes("date");

/** Formats a query table cell value, parsing dates when the column key contains date. @param {string} key - column field name; @param {*} value - raw cell value */
const formatQueryCellValue = (key, value) => {
  if (isObjectValue(value) || !isDateColumnKey(key)) return value;
  if (value === null || value === undefined || value === "") return value;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DEFAULT_DATE_FORMAT) : value;
};

/** Maps a query result row for table display with formatted date values. @param {Object} item - raw query row; @param {number} index - row index */
const mapQueryTableRow = (item, index) => (
  Object.entries(item || {}).reduce((row, [key, value]) => ({
    ...row,
    [key]: formatQueryCellValue(key, value),
  }), { __rowKey: index })
);

/** Collects scalar column keys from query result rows. @param {Array<Object>} queryRows - query result data rows */
const getQueryColumnKeys = (queryRows = []) => (
  Array.from(new Set(queryRows.flatMap((item) => Object.keys(item || {}))))
    .filter((key) => queryRows.some((item) => !isObjectValue(item?.[key])))
);

/** Builds Ant Design table columns from query keys, capped at maxColumns. @param {string[]} columnKeys - column field names; @param {number} [maxColumns=MAX_TABLE_COLUMNS] - max columns to display */
const buildQueryTableColumns = (columnKeys, maxColumns = MAX_TABLE_COLUMNS) => (
  columnKeys.slice(0, maxColumns).map((key) => ({
    title: startCase(key),
    dataIndex: key,
    key,
    render: (value) => (isObjectValue(value) ? "" : value),
  }))
);

/** Downloads the query_result.json file from S3. @param {Object} queryResultFile - S3 file descriptor with path, bucket, and region; @param {string|null} promptId - fallback for the downloaded filename */
const downloadQueryResultJson = async (queryResultFile, promptId) => {
  if (!queryResultFile?.path) return;

  const { bucket, region, path } = queryResultFile;
  const fileName = path.split("/").pop() || `query_result_${promptId || moment().format("MMDDYYYY_HHmmss")}.json`;
  const { success } = await downloadFileFromS3(path, { bucket, region }, fileName);

  if (!success) {
    toast.error("Failed to download query result file.");
  }
};

/** Exports full query result rows to an Excel file. @param {Object} queryResultData - query_summary and data rows; @param {string|null} promptId - used in the downloaded filename */
const downloadQueryResultExcel = (queryResultData, promptId) => {
  const queryRows = queryResultData?.data || [];
  if (!queryRows.length) return;

  const columnKeys = getQueryColumnKeys(queryRows);
  const exportRows = queryRows.map((row) => (
    columnKeys.reduce((acc, key) => ({
      ...acc,
      [startCase(key)]: isObjectValue(row?.[key]) ? "" : formatQueryCellValue(key, row[key]),
    }), {})
  ));

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Results");

  const fileSuffix = promptId || moment().format("MMDDYYYY_HHmmss");
  XLSX.writeFile(workbook, `ai_analytics_results_${fileSuffix}.xlsx`);
};

/** Renders chart, table, and error state for a single prompt result. @param {Object} promptResult - resolved prompt with plot/query data; @param {boolean} isLoading - shows spinner below the header; @param {Function} onRetry - retries failed generation; @param {Function} onRerun - reruns a completed prompt; @param {Function} onReloadCurrentPrompt - reloads S3 files for the prompt */
function PromptResultSection({
  promptResult,
  isLoading = false,
  onRetry,
  // onRerun,
  onReloadCurrentPrompt,
}) {
  const {
    prompt_id: promptId,
    prompt,
    timestamp,
    user_name: userName,
    status,
    error,
    plotData,
    queryResultData,
    queryResultFile,
  } = promptResult;

  const statusConfig = status && PROMPT_STATUS_CONFIG[status];
  const statusPending = status === "pending" || status === "in_process";
  const isPendingState = statusPending || error?.error_type === "PENDING";

  const queryTableRows = useMemo(() => (
    (queryResultData?.data || []).map(mapQueryTableRow)
  ), [queryResultData]);

  const queryTableColumns = useMemo(() => {
    const queryRows = queryResultData?.data || [];
    return buildQueryTableColumns(getQueryColumnKeys(queryRows));
  }, [queryResultData]);

  const hasDownloadableData = (queryResultData?.data || []).length > 0;
  const hasDownloadableJson = Boolean(queryResultFile?.path);
  const isFileError = error?.error_type === "FILE_LOAD_ERROR";
  const isSessionLoadError = error?.error_type === "SESSION_LOAD_ERROR";
  const showErrorActions = isPendingState || !isSessionLoadError;
  const showReloadAction = isPendingState || isFileError;

  const plotLayout = useMemo(
    () => buildPlotLayout(plotData?.layout),
    [plotData?.layout], // eslint-disable-line
  );

  return (
    <section className="AIAnalyticsPromptResultPanel__PromptSection">
      <div className="AIAnalyticsPromptResultPanel__PromptSectionHeader">
        <div className="AIAnalyticsPromptResultPanel__Prompt">{prompt}</div>
        <div className="AIAnalyticsPromptResultPanel__ActionPanel">
          {timestamp && (
            <span className="AIAnalyticsPromptSidebar__Pill">
              {formatTimestamp(timestamp)}
            </span>
          )}
          {userName && (
            <span className="AIAnalyticsPromptSidebar__Pill">
              {userName}
            </span>
          )}
          {statusConfig && (
            <span
              className={`AIAnalyticsPromptSidebar__Status AIAnalyticsPromptSidebar__Status--${statusConfig.className}`}
            >
              {statusConfig.label}
            </span>
          )}
          {/* {status === "done" && (
            <Tooltip title="Rerun prompt" placement="top">
              <Button
                type="text"
                size="middle"
                icon={<ReloadOutlined />}
                onClick={() => onRerun(promptId, prompt)}
              />
            </Tooltip>
          )} */}
        </div>
      </div>
      <div className="AIAnalyticsPromptResultPanel__PromptSectionBody">
        {isLoading && (
          <div className="AIAnalyticsPromptResultPanel__SectionLoading">
            <Spin size="large" />
          </div>
        )}
        {!isLoading && error && (
          <div
            className={`AIAnalyticsPromptResultPanel__Error${isPendingState ? " AIAnalyticsPromptResultPanel__Error--Pending" : ""}`}
          >
            <div>
              <h3>{isPendingState ? "Result is being generated" : "Unable to generate result"}</h3>
              <p>{error.error_message}</p>
            </div>
            {showErrorActions && (
              <div className="AIAnalyticsPromptResultPanel__ErrorActions">
                <Button
                  type="primary"
                  onClick={showReloadAction ? () => onReloadCurrentPrompt(promptId, status) : () => onRetry(promptId, prompt)}
                >
                  {showReloadAction ? "Reload" : "Retry"}
                </Button>
              </div>
            )}
          </div>
        )}
        {!isLoading && !error && plotData?.data && (
          <>
            <Divider />
            <div className="AIAnalyticsPromptResultPanel__ChartCard">
              <Plot
                data={plotData.data}
                layout={plotLayout}
                config={{ displayModeBar: true, responsive: true }}
                className="AIAnalyticsPromptResultPanel__Plot"
                useResizeHandler
              />
            </div>
          </>
        )}
        {!isLoading && !error && queryResultData?.query_summary && (
          <>
            <Divider />
            <div className="AIAnalyticsPromptResultPanel__QueryResultTable">
              <div className="AIAnalyticsPromptResultPanel__QueryResultTableHeader">
                <h3>{queryResultData.query_summary}</h3>
                <div className="AIAnalyticsPromptResultPanel__QueryResultButtonPanel">
                  <Tooltip title="Download JSON" placement="top">
                    <Button
                      type="text"
                      size="middle"
                      icon={<DownloadOutlined />}
                      disabled={!hasDownloadableJson}
                      onClick={() => downloadQueryResultJson(queryResultFile, promptId)}
                    >
                      JSON
                    </Button>
                  </Tooltip>
                  <Tooltip title="Download Excel" placement="top">
                    <Button
                      type="text"
                      size="middle"
                      icon={<DownloadOutlined />}
                      disabled={!hasDownloadableData}
                      onClick={() => downloadQueryResultExcel(queryResultData, promptId)}
                    >
                      Excel
                    </Button>
                  </Tooltip>
                </div>
              </div>
              <Table
                columns={queryTableColumns}
                dataSource={queryTableRows}
                pagination={{
                  defaultPageSize: DEFAULT_QUERY_TABLE_PAGE_SIZE,
                  pageSizeOptions: QUERY_TABLE_PAGE_SIZE_OPTIONS,
                  showSizeChanger: true,
                }}
                rowKey="__rowKey"
                size="middle"
                bordered
                rowHoverable
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/** Renders the scrollable result panel for one or more prompts. @param {Array} prompts - resolved prompt results; @param {boolean} isPromptLoading - shows loading overlay; @param {Function} onRetry - retries failed generation; @param {Function} onRerun - reruns a completed prompt; @param {Function} onReloadCurrentPrompt - reloads S3 files for a prompt */
export default function AIAnalyticsPromptResultPanel({
  // sessionTitle = "",
  prompts = [],
  isPromptLoading = false,
  onRetry,
  onRerun,
  onReloadCurrentPrompt,
}) {
  return (
    <div className="AIAnalyticsPromptResultPanel">
      <Scrollbar
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
        className="AIAnalyticsPromptResultPanel__Scrollbar"
      >
        <div className="AIAnalyticsPromptResultPanel__Body">
          {prompts.map((promptResult, index) => (
            <PromptResultSection
              key={promptResult.prompt_id || `prompt-${index}`}
              promptResult={promptResult}
              isLoading={isPromptLoading && index === prompts.length - 1}
              onRetry={onRetry}
              onRerun={onRerun}
              onReloadCurrentPrompt={onReloadCurrentPrompt}
            />
          ))}
        </div>
      </Scrollbar>
    </div>
  );
}
